import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { inputClass, labelClass, listRowClass, inlineSuccessClass, inlineErrorClass } from '@/components/ui/inputStyles';

const PER_PAGE = 20;

type UserRow = {
  id: number;
  username: string;
  role: string;
  owned_sites_count: number;
  account_enabled: boolean;
  created_at: string;
  account_valid_until?: string | null;
  /** 普通用户自建站点上限 */
  max_owned_sites?: number;
  media_quota_bytes?: number | null;
  media_storage_mode?: string;
};

type ListResp = {
  ok: boolean;
  items: UserRow[];
  total: number;
  page: number;
  per_page: number;
};

export function UsersPage() {
  const { capabilities, loading: sessLoading } = useSession();
  const [items, setItems] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newValidUntil, setNewValidUntil] = useState('');
  const [newMaxSites, setNewMaxSites] = useState('20');
  const [newMediaQuotaMb, setNewMediaQuotaMb] = useState('');
  const [mediaQuotaDraft, setMediaQuotaDraft] = useState<Record<number, string>>({});
  const [addSaving, setAddSaving] = useState(false);
  const [validDraft, setValidDraft] = useState<Record<number, string>>({});
  const [maxSitesDraft, setMaxSitesDraft] = useState<Record<number, string>>({});

  const load = useCallback(
    async (override?: { page?: number; q?: string }) => {
      const p = override?.page ?? page;
      const qRaw = override?.q !== undefined ? override.q : appliedQ;
      const q = qRaw.trim();
      setLoading(true);
      setErr('');
      try {
        const r = await apiFetch<ListResp>('list_system_users', {
          method: 'GET',
          query: {
            page: p,
            per_page: PER_PAGE,
            q: q || undefined,
          },
        });
        if (r.ok) {
          const tp = Math.max(1, Math.ceil(r.total / r.per_page));
          const safePage = Math.min(p, tp);
          if (safePage !== p) {
            setPage(safePage);
            return;
          }
          setPage(safePage);
          setItems(r.items);
          setTotal(r.total);
        } else {
          setErr('加载用户列表失败');
        }
      } catch {
        setErr('加载用户列表失败');
      } finally {
        setLoading(false);
      }
    },
    [page, appliedQ]
  );

  useEffect(() => {
    if (sessLoading || !capabilities.manage_users) return;
    void load();
  }, [load, sessLoading, capabilities.manage_users]);

  function runSearch() {
    setAppliedQ(qInput.trim());
    setPage(1);
  }

  function openAddModal() {
    setNewUser('');
    setNewPass('');
    setNewValidUntil('');
    setNewMaxSites('20');
    setNewMediaQuotaMb('');
    setAddOpen(true);
  }

  async function submitCreateUser() {
    const u = newUser.trim();
    const pw = newPass.trim();
    if (pw !== '' && pw.length < 6) {
      setErr('初始密码至少 6 位，或留空使用默认 88888888');
      return;
    }
    let maxSites = parseInt(newMaxSites.trim(), 10);
    if (Number.isNaN(maxSites) || maxSites < 1) {
      setErr('站点数量上限须为 1～500 的整数');
      return;
    }
    if (maxSites > 500) {
      maxSites = 500;
    }
    setAddSaving(true);
    setMsg('');
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('create_system_user', {
        method: 'POST',
        body: JSON.stringify({
          username: u,
          max_owned_sites: maxSites,
          ...(pw !== '' ? { password: pw } : {}),
          ...(newValidUntil.trim() !== '' ? { account_valid_until: newValidUntil.trim() } : {}),
          ...(newMediaQuotaMb.trim() !== ''
            ? { media_quota_bytes: Math.max(1, parseInt(newMediaQuotaMb.trim(), 10) || 0) * 1048576 }
            : {}),
        }),
      });
      if (r.ok) {
        setMsg('已创建普通用户（默认密码 88888888，若填写了初始密码则以填写为准）；对方登录后需自行创建站点。');
        setAddOpen(false);
        setAppliedQ('');
        setQInput('');
        await load({ page: 1, q: '' });
      } else {
        setErr(r.err === 'username_exists' ? '用户名已存在' : '创建失败');
      }
    } catch {
      setErr('请求失败');
    } finally {
      setAddSaving(false);
    }
  }

  async function toggleEnabled(id: number, enabled: boolean) {
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('update_system_user', {
        method: 'POST',
        body: JSON.stringify({ id, account_enabled: enabled }),
      });
      if (r.ok) {
        setMsg(enabled ? '已启用' : '已禁用');
        await load();
      } else {
        const m: Record<string, string> = {
          cannot_disable_self: '不能禁用当前登录账号',
          last_admin_required: '至少保留一名可用管理员',
        };
        setErr(m[r.err || ''] || '更新失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function resetPassword(id: number, username: string) {
    if (!window.confirm(`将用户「${username}」的密码重置为 88888888？`)) return;
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('reset_system_user_password', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      if (r.ok) {
        setMsg('已重置密码为 88888888');
        await load();
      } else {
        const m: Record<string, string> = {
          invalid_target: '只能重置普通用户密码',
        };
        setErr(m[r.err || ''] || '重置失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function saveUserMediaQuota(u: UserRow) {
    if (u.role !== 'user') return;
    setErr('');
    const raw = (mediaQuotaDraft[u.id] !== undefined ? mediaQuotaDraft[u.id]! : '').trim();
    try {
      const body: { id: number; media_quota_bytes: number | null } = { id: u.id, media_quota_bytes: null };
      if (raw !== '') {
        const mb = parseInt(raw, 10);
        if (Number.isNaN(mb) || mb < 1) {
          setErr('多媒体限额须为 ≥1 的 MB 整数，或留空使用系统默认');
          return;
        }
        body.media_quota_bytes = mb * 1048576;
      }
      const r = await apiFetch<{ ok: boolean }>('update_system_user', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (r.ok) {
        setMsg('已保存多媒体限额');
        setMediaQuotaDraft((d) => {
          const x = { ...d };
          delete x[u.id];
          return x;
        });
        await load();
      } else {
        setErr('保存失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function saveUserMaxSites(u: UserRow) {
    if (u.role !== 'user') return;
    setErr('');
    const raw = (maxSitesDraft[u.id] !== undefined ? maxSitesDraft[u.id]! : String(u.max_owned_sites ?? 20)).trim();
    let n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1) {
      setErr('站点上限须为 1～500 的整数');
      return;
    }
    if (n > 500) {
      n = 500;
    }
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('update_system_user', {
        method: 'POST',
        body: JSON.stringify({ id: u.id, max_owned_sites: n }),
      });
      if (r.ok) {
        setMsg('已保存站点数量上限');
        setMaxSitesDraft((d) => {
          const x = { ...d };
          delete x[u.id];
          return x;
        });
        await load();
      } else {
        setErr('保存失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function saveUserValidUntil(u: UserRow) {
    if (u.role !== 'user') return;
    setErr('');
    const raw = validDraft[u.id] !== undefined ? validDraft[u.id]! : (u.account_valid_until ?? '');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('update_system_user', {
        method: 'POST',
        body: JSON.stringify({
          id: u.id,
          account_valid_until: raw.trim() === '' ? null : raw.trim(),
        }),
      });
      if (r.ok) {
        setMsg('已保存有效期设置');
        setValidDraft((d) => {
          const n = { ...d };
          delete n[u.id];
          return n;
        });
        await load();
      } else {
        setErr(r.detail || '保存失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  async function removeUser(id: number, username: string, role: string) {
    if (role === 'admin') {
      if (!window.confirm(`确定删除管理员「${username}」？不可恢复。`)) return;
    } else {
      if (!window.confirm(`确定删除用户「${username}」？不可恢复。`)) return;
    }
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('delete_system_user', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      if (r.ok) {
        setMsg('已删除');
        await load();
      } else {
        const m: Record<string, string> = {
          cannot_delete_self: '不能删除当前登录账号',
          last_admin_required: '至少保留一名管理员',
          user_has_sites: r.detail || '该用户仍拥有站点，请先删除其名下站点后再删账号',
        };
        setErr(m[r.err || ''] || '删除失败');
      }
    } catch {
      setErr('请求失败');
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  if (!sessLoading && !capabilities.manage_users) {
    return <Navigate to="/dashboard" replace />;
  }

  if (sessLoading) {
    return <Spinner label="验证权限" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="用户管理"
        description="创建普通用户账号；各用户登录后自行创建并管理自己的站点，数据彼此隔离。不能删除当前登录账号。"
      />

      <PcRecommendedCallout>
        用户管理涉及账号、密码、有效期与权限，表格列较多；新建与编辑建议在电脑浏览器中完成。
      </PcRecommendedCallout>

      <Card
        title="账号列表"
        titleExtra={
          <Button type="button" variant="primary" size="sm" onClick={openAddModal}>
            新建普通用户
          </Button>
        }
      >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className={`${labelClass} flex-1`}>
            <span>关键词（用户名 / ID / 角色）</span>
            <input
              className={inputClass}
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              placeholder="输入后点击查询"
              autoComplete="off"
            />
          </label>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={runSearch}>
              查询
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setQInput('');
                setAppliedQ('');
                setPage(1);
              }}
            >
              重置
            </Button>
          </div>
        </div>
        {loading ? (
          <Spinner label="加载中" />
        ) : (
          <div className="space-y-2">
            {items.length === 0 ? (
              <p className="text-sm text-slate-500">暂无用户</p>
            ) : (
              items.map((u) => (
                <div key={u.id} className={listRowClass}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">
                      {u.username}{' '}
                      <span className="text-xs font-normal text-slate-500">
                        · {u.role === 'admin' ? '管理员' : '普通用户'}
                        {u.account_enabled ? '' : ' · 已禁用'}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {u.role === 'user'
                        ? `名下站点：${u.owned_sites_count} / 上限 ${u.max_owned_sites ?? 20} · ${
                            u.account_valid_until ? `有效期至 ${u.account_valid_until}（含当日）` : '有效期不限'
                          }`
                        : '可管理全部站点（系统管理员）'}
                    </div>
                    {u.role === 'user' ? (
                      <div className="mt-2 flex flex-wrap items-end gap-2">
                        <label className="mb-0 min-w-0 flex-1 sm:max-w-[10rem]">
                          <span className="text-[11px] text-slate-500">站点数量上限（1～500）</span>
                          <input
                            type="number"
                            min={1}
                            max={500}
                            className={`${inputClass} mt-0.5 text-xs`}
                            value={
                              maxSitesDraft[u.id] !== undefined
                                ? maxSitesDraft[u.id]!
                                : String(u.max_owned_sites ?? 20)
                            }
                            onChange={(e) =>
                              setMaxSitesDraft((d) => ({
                                ...d,
                                [u.id]: e.target.value,
                              }))
                            }
                          />
                        </label>
                        <Button size="sm" variant="secondary" type="button" onClick={() => void saveUserMaxSites(u)}>
                          保存上限
                        </Button>
                      </div>
                    ) : null}
                    {u.role === 'user' ? (
                      <div className="mt-2 flex flex-wrap items-end gap-2">
                        <label className="mb-0 min-w-0 flex-1 sm:max-w-[14rem]">
                          <span className="text-[11px] text-slate-500">调整有效期至</span>
                          <input
                            type="date"
                            className={`${inputClass} mt-0.5 text-xs`}
                            value={validDraft[u.id] !== undefined ? validDraft[u.id]! : (u.account_valid_until ?? '')}
                            onChange={(e) =>
                              setValidDraft((d) => ({
                                ...d,
                                [u.id]: e.target.value,
                              }))
                            }
                          />
                        </label>
                        <Button size="sm" variant="secondary" type="button" onClick={() => void saveUserValidUntil(u)}>
                          保存有效期
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setValidDraft((d) => ({ ...d, [u.id]: '' }));
                            void (async () => {
                              setErr('');
                              try {
                                const r = await apiFetch<{ ok: boolean; err?: string }>('update_system_user', {
                                  method: 'POST',
                                  body: JSON.stringify({ id: u.id, account_valid_until: null }),
                                });
                                if (r.ok) {
                                  setMsg('已设为有效期不限');
                                  setValidDraft((d) => {
                                    const n = { ...d };
                                    delete n[u.id];
                                    return n;
                                  });
                                  await load();
                                } else setErr('更新失败');
                              } catch {
                                setErr('请求失败');
                              }
                            })();
                          }}
                        >
                          清除（不限）
                        </Button>
                      </div>
                    ) : null}
                    {u.role === 'user' ? (
                      <div className="mt-2 flex flex-wrap items-end gap-2">
                        <label className="mb-0 min-w-0 flex-1 sm:max-w-[12rem]">
                          <span className="text-[11px] text-slate-500">
                            多媒体限额 MB（留空=系统默认
                            {(u.media_storage_mode === 'oss' || u.media_storage_mode === 'oss_cdn') ? ' · 已 OSS' : ''}）
                          </span>
                          <input
                            type="number"
                            min={1}
                            className={`${inputClass} mt-0.5 text-xs`}
                            value={
                              mediaQuotaDraft[u.id] !== undefined
                                ? mediaQuotaDraft[u.id]!
                                : u.media_quota_bytes != null
                                  ? String(Math.round(u.media_quota_bytes / 1048576))
                                  : ''
                            }
                            onChange={(e) =>
                              setMediaQuotaDraft((d) => ({
                                ...d,
                                [u.id]: e.target.value,
                              }))
                            }
                          />
                        </label>
                        <Button size="sm" variant="secondary" type="button" onClick={() => void saveUserMediaQuota(u)}>
                          保存限额
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {u.role === 'user' ? (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void toggleEnabled(u.id, !u.account_enabled)}
                        >
                          {u.account_enabled ? '禁用' : '启用'}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void resetPassword(u.id, u.username)}
                        >
                          重置密码
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => void removeUser(u.id, u.username, u.role)}>
                          删除
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">内置管理员请在数据库侧管理</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        <PaginationBar
          page={page}
          totalPages={totalPages}
          total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          summary={total > 0 ? `共 ${total} 条，每页 ${PER_PAGE} 条` : undefined}
        />
      </Card>

      <Modal
        open={addOpen}
        title="新建普通用户"
        onClose={() => !addSaving && setAddOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" disabled={addSaving} onClick={() => setAddOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={addSaving || !newUser.trim() || (newPass.trim() !== '' && newPass.trim().length < 6)}
              onClick={() => void submitCreateUser()}
            >
              {addSaving ? '创建中…' : '创建'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            无需预先绑定站点。普通用户首次登录后请在「站点管理」中自行创建站点。
          </p>
          <label className={labelClass}>
            <span>用户名</span>
            <input
              className={inputClass}
              value={newUser}
              onChange={(e) => setNewUser(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className={labelClass}>
            <span>初始密码（可选，≥6 位；留空则为 88888888）</span>
            <input
              type="password"
              className={inputClass}
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              {...(newPass.trim() !== '' ? { minLength: 6 } : {})}
              autoComplete="new-password"
            />
          </label>
          <label className={labelClass}>
            <span>有效期至（可选，留空表示不限）</span>
            <input type="date" className={inputClass} value={newValidUntil} onChange={(e) => setNewValidUntil(e.target.value)} />
          </label>
          <label className={labelClass}>
            <span>站点数量上限（1～500，默认 20）</span>
            <input
              type="number"
              min={1}
              max={500}
              className={inputClass}
              value={newMaxSites}
              onChange={(e) => setNewMaxSites(e.target.value)}
            />
          </label>
          <label className={labelClass}>
            <span>多媒体本地限额 MB（可选，留空使用系统默认）</span>
            <input
              type="number"
              min={1}
              className={inputClass}
              value={newMediaQuotaMb}
              onChange={(e) => setNewMediaQuotaMb(e.target.value)}
              placeholder="例如 512"
            />
          </label>
        </div>
      </Modal>

      {msg ? <p className={inlineSuccessClass}>{msg}</p> : null}
      {err ? <p className={inlineErrorClass}>{err}</p> : null}
    </div>
  );
}
